// Everything the pages need from the backend. Two implementations: Supabase (the real thing) and a demo with made-up
// data that never leaves the browser. Pages only know this interface.
import type {
  CustomerList,
  CustomerPage,
  CustomerQuery,
  FollowUpInput,
  FollowUpList,
  FollowUpQuery,
  Insights,
  Label,
  MessageTemplate,
  MessageTemplateInput,
  VisitResult,
} from "./crm";
import type { Disposition, DispositionCall, Dispositions } from "./dispositions";
import type {
  AddOns,
  BillingSummary,
  CallDetail,
  CallNote,
  CompanyHealth,
  CompanyHealthDetail,
  CompanyTeamMember,
  DashboardRange,
  Digest,
  EraseResult,
  FacebookAssign,
  FacebookChosen,
  FacebookLogin,
  FacebookPageChoice,
  FacebookState,
  HealthOverview,
  ImportOptions,
  ImportResult,
  ImportRow,
  IndiaMartRead,
  IntegrationActivityItem,
  IntegrationDemand,
  IntegrationsState,
  Invoice,
  LeadDetail,
  LeadEnquiry,
  LeadImport,
  LeadListItem,
  LeadQuery,
  LiveCall,
  Member,
  MyOperatorConnected,
  NoteLayer,
  ObjectionQuotes,
  Org,
  Overview,
  PlatformAuditRow,
  PlatformHealth,
  PlatformInvoice,
  PlatformOrg,
  QaCall,
  QaCheck,
  QaLayer,
  QaMe,
  QaNext,
  QaRole,
  QaStage,
  QaSummary,
  RawCallRow,
  RecentEnquiries,
  RecordingCounts,
  ReviewAction,
  Role,
  SettingsBundle,
  Source,
  Stage,
  Status,
  TaskType,
  TaskWithLead,
  Taxonomy,
  Team,
  TeamHealth,
  Usage,
  WebhookFields,
  WebhookRetried,
  WebhookState,
} from "./types";
import type { Json } from "./database.types";
import type { PlatformAiUsage } from "./aiUsage";
import type { UseCase } from "./integrations";
import type {
  AiInstructions,
  BusinessType,
  BusinessTypeResult,
  CallType,
  ChecklistTally,
  TextField,
} from "./aiInstructions";

export const PAGE_SIZE = 50;
/** Rows per page of the raw calls list (D-077). */
export const RAW_CALLS_PAGE = 50;

export interface SignInTarget {
  email?: string;
  phone?: string;
}

export interface MfaState {
  current: string | null;
  next: string | null;
  verifiedFactorId: string | null;
}

/** A lead on the analysis review page: what the engine holds on it, and each call it read (D-195). */
export interface ReviewLead {
  lead_id: string;
  name: string | null;
  // deno-lint-ignore no-explicit-any
  state: Record<string, any>;
  calls: ReviewCall[];
}

export interface ReviewCall {
  recording_id: string;
  started_at: string | null;
  direction: string | null;
  duration_s: number | null;
  caller: string | null;
  status: string;
  pass: string;
  skip_reason: string | null;
  // deno-lint-ignore no-explicit-any
  record: Record<string, any> | null;
  transcript: string | null;
  feedback: { is_right: boolean; note: string | null } | null;
}

export interface Api {
  readonly demo: boolean;

  // Session
  hasSession(): Promise<boolean>;
  onSessionChange(listener: () => void): () => void;
  sendCode(target: SignInTarget): Promise<void>;
  verifyCode(target: SignInTarget, code: string): Promise<void>;
  signOut(): Promise<void>;
  status(): Promise<Status>;
  refresh(): Promise<void>;
  mfaState(): Promise<MfaState>;
  mfaEnroll(): Promise<{ factorId: string; qr: string; secret: string }>;
  mfaVerify(factorId: string, code: string): Promise<void>;
  createOrganization(
    input: { name: string; displayName: string; timezone: string },
  ): Promise<void>;

  // An organization's data (row-level security decides what each person sees)
  org(): Promise<Org>;
  members(): Promise<Member[]>;
  overview(): Promise<Overview>;
  /** The dashboard for one day or a range of days, "YYYY-MM-DD" in the company's time zone (D-144). */
  dashboardRange(from: string, to: string): Promise<DashboardRange>;
  /** The leads of the last hours (24 unless said), newest first: the team's for managers, her own for a telecaller. */
  recentEnquiries(hours?: number): Promise<RecentEnquiries>;
  /** The company's integration requests and connections; owners and admins only (D-146). */
  integrationsState(): Promise<IntegrationsState>;
  /** Ask for an integration (what it should do, and a note), or add a note to the company's request. */
  requestIntegration(key: string, useCase: UseCase | null, note: string | null): Promise<void>;
  /** Count an owner's or admin's look at an integration. */
  openIntegration(key: string): Promise<void>;
  /** Connect MyOperator with its Public API token and the numbers its IVR rings the team from (D-147). */
  connectMyOperator(token: string, ivrNumbers: string[]): Promise<MyOperatorConnected>;
  /** Read MyOperator's call log now and match the IVR calls. */
  syncMyOperator(): Promise<{ logs?: number; moved?: number; error?: string }>;
  /** The IVR numbers, and which telecaller each MyOperator user is ({user id: member id or null}). */
  updateMyOperatorSettings(ivrNumbers: string[], userMembers: Record<string, string | null>): Promise<void>;
  disconnectIntegration(key: string): Promise<void>;
  /** Facebook Lead Ads (D-148): the connection, its Pages and forms, who gets new customers, the latest leads. */
  facebookState(): Promise<FacebookState>;
  /** Where to send the owner to log in with Facebook; Facebook comes back to redirectUri. */
  facebookStart(redirectUri: string): Promise<string>;
  /** Facebook came back: the login is kept, and the Pages it manages are listed to choose from. */
  facebookFinish(code: string, state: string): Promise<FacebookLogin>;
  /** The Pages the kept login manages, to add one. */
  facebookPages(): Promise<FacebookPageChoice[]>;
  /** Connect the chosen Pages: each is subscribed to its leads and its forms are read. */
  facebookChoose(pageIds: string[]): Promise<FacebookChosen[]>;
  facebookRemovePage(pageId: string): Promise<void>;
  /** The forms a Page brings leads from; null: every form. */
  facebookSetForms(pageId: string, formIds: string[] | null): Promise<void>;
  facebookSetAssignment(mode: FacebookAssign["mode"], members: string[]): Promise<void>;
  /** Pause an integration: Voxowa stops acting on it, keeping what arrives meanwhile (D-157). */
  pauseIntegration(key: string): Promise<void>;
  resumeIntegration(key: string): Promise<void>;
  /** Every change to an integration, newest first (D-157). */
  integrationActivity(key: string): Promise<IntegrationActivityItem[]>;
  /** Who gets one form's new customers (D-154): its own choice, or "default" for the company's. */
  facebookSetFormAssignment(formId: string, mode: FacebookAssign["mode"] | "default", members: string[]): Promise<void>;
  /** Bring in the leads of the last days; given out like new ones, or left with nobody. */
  facebookImport(days: 7 | 30 | 90, assign: boolean): Promise<void>;
  /** Read every connected Page now for leads not yet in Voxowa. */
  facebookSync(): Promise<void>;
  /** Disconnect: every Page stops sending leads and every token is removed. */
  facebookDisconnect(): Promise<void>;
  /** A webhook integration (D-158): its address, who gets new customers, the fields chosen and the latest leads. */
  webhookState(key: string): Promise<WebhookState>;
  /** Turn it on: the company's own address, waiting for its first lead. */
  webhookConnect(key: string): Promise<WebhookState>;
  /** A new address; the old one stops at once. */
  webhookNewAddress(key: string): Promise<WebhookState>;
  webhookSetAssignment(key: string, mode: FacebookAssign["mode"], members: string[]): Promise<void>;
  /** Which fields hold the number, name, email and city; the leads that could not be read are tried again. */
  webhookSetFields(key: string, fields: WebhookFields): Promise<WebhookRetried>;
  /** Try the waiting and unreadable leads again. */
  webhookRetry(key: string): Promise<WebhookRetried>;
  /** IndiaMART's Pull API key (D-176): checked with IndiaMART, kept, and the last 7 days' leads brought in. */
  indiamartSaveKey(key: string): Promise<IndiaMartRead>;
  /** IndiaMART's read now, at most once in 5 minutes. */
  indiamartCheckNow(): Promise<IndiaMartRead>;
  indiamartRemoveKey(): Promise<void>;
  /** The quotes behind one of the overview's top objections, from the same 7 days and the same people. */
  objectionQuotes(category: string): Promise<ObjectionQuotes>;
  leads(query: LeadQuery): Promise<{ rows: LeadListItem[]; total: number }>;
  lead(id: string): Promise<LeadDetail>;
  updateLead(
    id: string,
    patch: {
      stage?: Stage;
      owner_member_id?: string | null;
      name?: string | null;
    },
  ): Promise<void>;
  addLead(
    input: { phone: string; name?: string; ownerMemberId?: string },
  ): Promise<string>;
  /** Erases a customer on request (PRIV-01): owners and admins only. The audio goes within the hour. */
  eraseLead(id: string, reason: string, neverRecord: boolean): Promise<EraseResult>;

  // The CRM, as on the phone (D-163). A telecaller reaches only the customers assigned to her.
  /** The customers she may see, found and filtered, a page at a time, with the counts per stage, owner and source. */
  customerList(query: CustomerQuery): Promise<CustomerList>;
  /** One customer as the phone shows it: stage, label, people and numbers, details, open follow-ups and history. */
  customerPage(id: string): Promise<CustomerPage>;
  /** Her follow-ups, or (owners, admins and managers) anyone's or the team's, with each person's counts. */
  followUps(query: FollowUpQuery): Promise<FollowUpList>;
  /** The owner's numbers for a period (ISO times): pipeline, sources, speed to call and each person's discipline. */
  insights(from: string, to: string): Promise<Insights>;
  /** Plans a follow-up (a new call replaces the open one; visits and meetings sit beside it) or changes one. */
  saveFollowUp(input: FollowUpInput): Promise<void>;
  /** Done; a visit or meeting says whether it happened. */
  completeFollowUp(id: string, result?: VisitResult | null): Promise<void>;
  snoozeFollowUp(id: string, untilIso: string): Promise<void>;
  cancelFollowUp(id: string): Promise<void>;
  /** Moves a customer to a stage; a lost one may say why, and its open follow-ups are cancelled. */
  setStage(leadId: string, stageId: string, reason?: string | null): Promise<void>;
  /** Her own Hot, Warm or Cold; null removes it. */
  setLabel(leadId: string, label: Label | null): Promise<void>;
  /** The name and the changed details (an empty one removes it): the customer's owner or a manager. */
  saveCustomerDetails(leadId: string, name: string | null, details: Record<string, string>): Promise<void>;
  addCustomerNumber(leadId: string, phone: string, person?: string, role?: string): Promise<void>;
  removeCustomerNumber(numberId: string): Promise<void>;
  addNote(leadId: string, text: string): Promise<void>;
  /** Hands customers to one person, or to nobody; their open follow-ups go along. Owners, admins and managers. */
  assignCustomers(leadIds: string[], ownerId: string | null): Promise<number>;
  /** The company's WhatsApp messages, in its order, to send from a customer's page. */
  messageTemplates(): Promise<MessageTemplate[]>;
  /** Owners and admins (D-187): a message saved, a new one last; its id. */
  saveMessageTemplate(t: MessageTemplateInput): Promise<string>;
  /** Owners and admins: a message no longer offered. */
  removeMessageTemplate(id: string): Promise<void>;
  /** Owners and admins: the order the messages are offered in. */
  orderMessageTemplates(ids: string[]): Promise<void>;
  /** The company's dispositions after an answered and an unanswered call (D-188); empty lists: the standard ones. */
  dispositions(): Promise<Dispositions>;
  /** Owners and admins: one list saved whole, in its order; the list as saved. */
  saveDispositions(call: DispositionCall, list: Disposition[]): Promise<Disposition[]>;
  /** Keeps a WhatsApp message in the customer's history (null: one she wrote herself). */
  logMessage(leadId: string, templateId: string | null): Promise<void>;
  callDetail(recordingId: string): Promise<CallDetail>;
  playback(
    recordingId: string,
    t?: number | null,
  ): Promise<{ url: string; t: number | null }>;
  /**
   * The call log as the phones sent it, newest first, each call with its recording if it has one, and the recordings
   * that matched no call (D-077). Managers see everyone's; a telecaller their own.
   */
  rawCalls(query: { memberId?: string | null; before?: string | null; limit?: number }): Promise<RawCallRow[]>;
  /** How many recordings the phones sent and how far they got (D-077). */
  recordingCounts(): Promise<RecordingCounts>;
  /**
   * Runs a call's transcription and analysis again (REC-05): owners and admins. With a note, where the earlier reading
   * went wrong and what: kept in the call notes and given to the AI; a note on the analysis alone keeps the transcript
   * (D-111).
   */
  rerunRecording(recordingId: string, note?: { layer: NoteLayer; text: string }): Promise<void>;
  /** Every note owners and admins wrote when running a call again, newest first (D-111). */
  callNotes(): Promise<CallNote[]>;

  // Call review (D-112, QA-01 to QA-05): reviewers, owners and admins only; the server checks every call.
  /** What the signed-in member may do in call review. */
  qaMe(): Promise<QaMe>;
  /** The dashboard was opened: how much the owners and managers use it (D-182). At most every 10 minutes counts. */
  noteDashboardVisit(): Promise<void>;
  /** The next call to review at this stage, and how far the reviewer has got (QA-02). */
  qaNext(stage: QaStage): Promise<QaNext>;
  /** Everything a reviewer needs on one call (QA-01). */
  qaCall(recordingId: string): Promise<QaCall>;
  /** Saves the reviewer's answers; a No flags the call for the seniors (QA-03). Returns the next call. */
  qaSave(
    recordingId: string,
    stage: QaStage,
    review: { answers: Record<string, boolean>; note?: string; flag?: boolean; layer?: QaLayer | null },
  ): Promise<{ flagged: boolean; next: QaNext }>;
  /** A check for everyone, from then on (QA-04). */
  qaAddCheck(label: string): Promise<QaCheck>;
  /** Owners and admins make a member a tester or a senior, or neither. */
  qaSetRole(memberId: string, role: QaRole | null): Promise<void>;
  /** How the review is going (QA-05). */
  qaSummary(): Promise<QaSummary>;
  review(
    analysisId: string,
    action: ReviewAction,
    patch?: { type?: TaskType; dueAt?: string },
  ): Promise<void>;
  /** Open follow-ups of one member, or (null) of everyone the person may see. */
  openTasks(memberId: string | null): Promise<TaskWithLead[]>;
  completeTask(id: string): Promise<void>;
  snoozeTask(id: string, minutes: number): Promise<void>;
  rescheduleTask(id: string, dueAt: string, type?: TaskType): Promise<void>;
  addTask(
    leadId: string,
    type: TaskType,
    dueAt: string,
    reason?: string,
  ): Promise<void>;

  team(): Promise<Team>;
  /** Each person's phone app and today's calls, for owners, admins and managers (TEAM-12). */
  teamHealth(): Promise<TeamHealth>;
  invite(
    input: { displayName: string; role: Role; phone?: string; email?: string },
  ): Promise<void>;
  revokeInvitation(id: string): Promise<void>;
  revokeDevice(id: string): Promise<void>;
  removeMember(id: string): Promise<void>;
  setRole(memberId: string, role: Role): Promise<void>;

  importLeads(rows: ImportRow[], options?: ImportOptions): Promise<ImportResult>;
  /** The company's sources, for the import's own (D-097). */
  sources(): Promise<Source[]>;
  /** The latest imports, newest first: managers only. */
  leadImports(): Promise<LeadImport[]>;
  /** A customer's enquiries, newest first (LEAD-16). */
  leadEnquiries(leadId: string): Promise<LeadEnquiry[]>;
  settings(): Promise<SettingsBundle>;
  saveOrg(
    patch: {
      name?: string;
      timezone?: string;
      working_hours?: Json;
      settings?: Json;
    },
  ): Promise<void>;
  addHoliday(day: string, name: string): Promise<void>;
  deleteHoliday(id: string): Promise<void>;
  addExcluded(phone: string, reason: string): Promise<void>;
  deleteExcluded(id: string): Promise<void>;

  // What the AI is told (ORG-06..08, TEAM-10): everyone in the company reads it; owners and admins change it.
  aiInstructions(): Promise<AiInstructions>;
  saveAiInstructions(patch: Partial<Record<TextField, string>> & { checklist?: Json; facts?: Json }): Promise<void>;
  /** Every objection category, hidden ones too, in their order. */
  objectionCategories(): Promise<Taxonomy[]>;
  saveObjectionCategory(
    id: string,
    patch: { label?: string; description?: string | null; active?: boolean },
  ): Promise<void>;
  addObjectionCategory(label: string, description: string): Promise<void>;
  /** A person's call type and the note the AI reads with their calls. */
  setCallProfile(
    memberId: string,
    patch: { call_type?: CallType | null; ai_note?: string; work_name?: string | null },
  ): Promise<void>;
  /** Whether the in-call brief card shows on this person's phone (C17); on unless switched off. */
  setCallBrief(memberId: string, on: boolean): Promise<void>;

  /** The call she started from the app on her phone (WEB-07), or null. */
  liveCall(): Promise<LiveCall | null>;
  /** Calls back whenever her live call changes, until the returned function is called. */
  onLiveCall(memberId: string, onChange: () => void): () => void;
  /** Any change to the company's leads or tasks, as calls sync and after-call choices land (TEAM-12). */
  onTeamActivity(orgId: string, onChange: () => void): () => void;
  /** How often each checklist item was done, per person, over the last `days` days (DASH-05). */
  checklistSummary(days: number): Promise<ChecklistTally[]>;
  /** The company's business type, with its categories and checklist (ORG-09). Owners and admins. */
  setBusinessType(type: BusinessType): Promise<BusinessTypeResult>;
  usage(): Promise<Usage>;
  digests(): Promise<Digest[]>;

  // The analysis engine's review page (D-195): only for the person it names.
  analysisReview(): Promise<ReviewLead[]>;
  analysisFeedback(recordingId: string, isRight: boolean, note?: string): Promise<void>;
  billing(): Promise<{ summary: BillingSummary; invoices: Invoice[] }>;
  buyMinutes(pack: string): Promise<void>;

  // Platform admin (never tenant rows)
  platformOrgs(): Promise<PlatformOrg[]>;
  platformAudit(): Promise<PlatformAuditRow[]>;
  /** A new company with its industry and add-ons (PLAT-10, D-168); its owner is invited. */
  platformCreateOrg(
    input: {
      name: string;
      ownerName: string;
      ownerEmail?: string;
      ownerPhone?: string;
      vertical: BusinessType;
      addOns: AddOns;
      /** How many people it may have; null: no limit, 1: a team of one (D-169). */
      memberLimit: number | null;
    },
  ): Promise<void>;
  platformInviteOwner(
    input: {
      orgId: string;
      ownerName: string;
      ownerEmail?: string;
      ownerPhone?: string;
    },
  ): Promise<void>;
  platformSetStatus(
    orgId: string,
    status: "active" | "suspended",
    reason: string,
  ): Promise<void>;
  /** Approves a company that signed itself up (PLAT-09); returns how many of its waiting calls went to the AI. */
  platformApproveOrg(orgId: string): Promise<number>;
  platformSetMinutes(
    orgId: string,
    minutes: number,
    reason: string,
  ): Promise<void>;
  /** A company's add-ons, with a reason (PLAT-11); returns how many of its waiting calls went to the AI. */
  platformSetAddOns(orgId: string, addOns: AddOns, reason: string): Promise<number>;
  /** A company's people limit, with a reason (PLAT-12); returns how many unowned customers became its one person's. */
  platformSetMemberLimit(orgId: string, limit: number | null, reason: string): Promise<number>;
  /** A company's name, industry, time zone and country (PLAT-11). */
  platformUpdateOrg(
    orgId: string,
    input: { name: string; vertical: BusinessType; timezone: string; defaultCountry: string },
  ): Promise<void>;
  platformHealth(): Promise<PlatformHealth>;
  /** Every company's health and the week's totals (D-182): counts only, never a company's customers or calls. */
  platformHealthOverview(): Promise<HealthOverview>;
  /** One company's health with its last four weeks, day by day; no person in it. */
  platformCompanyHealth(orgId: string): Promise<CompanyHealthDetail>;
  /** A company's team for support (D-183): names, roles, last four digits and phones. Each look is in its history. */
  platformCompanyTeam(orgId: string): Promise<CompanyTeamMember[]>;
  /** Mark a company as Voxowa's own, or note a quiet spell until a date (D-182). */
  platformSetCompanyFlags(
    orgId: string,
    flags: { internal: boolean; quietUntil: string | null; note: string | null },
  ): Promise<CompanyHealth>;
  /** The AI usage record for a period (PLAT-07): models, tokens and cost, in total and per call. */
  platformAiUsage(from: string, to: string, limit?: number): Promise<PlatformAiUsage>;
  /** Pause or resume all AI processing (PLAT-08); resuming queues held recordings again. */
  platformSetAiMode(mode: "live" | "paused", reason: string): Promise<void>;
  platformInvoices(status?: "issued" | "paid" | "void"): Promise<PlatformInvoice[]>;
  /** Each integration's demand across companies, paid seats first (D-146). */
  platformIntegrationDemand(): Promise<IntegrationDemand[]>;
  platformMarkPaid(invoiceId: string, reference: string): Promise<void>;
  platformVoidInvoice(invoiceId: string, reason: string): Promise<void>;
}

/** An error to show the person as is. */
export class ApiError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
  }
}

/** True when the error means the session is gone and the person must sign in again. */
export function isSignedOut(e: unknown): boolean {
  return e instanceof ApiError &&
    (e.code === "signed_out" || e.code === "PGRST301" || e.code === "PGRST303");
}
